export type DecodeRequest = {
    expectedTimestamp: number;
    deferred: PromiseWithResolvers<VideoFrame>;
};

export class FrameCodec {
    private readonly decoder: VideoDecoder;
    private readonly pendingDecodes: DecodeRequest[] = [];

    constructor(config: VideoDecoderConfig) {
        this.decoder = new VideoDecoder({
            output: (frame) => this.handleOutput(frame),
            error: (error) => this.handleError(error),
        });

        this.decoder.configure(config);
    }

    decode(chunk: EncodedVideoChunk) {
        const deferred = Promise.withResolvers<VideoFrame>();

        const request: DecodeRequest = {
            expectedTimestamp: chunk.timestamp,
            deferred,
        };

        this.pendingDecodes.push(request);

        try {
            this.decoder.decode(chunk);

            // Chrome can accept a chunk without immediately emitting an output frame.
            // Flushing forces the decoder to drain queued work, which fixes black frames
            // on older videos where decode() alone leaves the promise pending.
            this.flush(request);
        } catch (error) {
            // If decode() throws synchronously, remove this request so it does not
            // stay pending forever and accidentally receive a later output frame.
            this.removeRequest(request);
            deferred.reject(error);
        }

        return deferred.promise;
    }

    close() {
        this.rejectPending(new Error('decoder closed'));

        if (this.decoder.state !== 'closed') {
            this.decoder.close();
        }
    }

    private flush(request: DecodeRequest) {
        const flush = this.decoder.flush?.bind(this.decoder);

        if (!flush) {
            return;
        }

        flush().catch((error) => {
            // Output may already have resolved this request before flush rejects.
            // Only reject it if it is still pending.
            if (!this.pendingDecodes.includes(request)) {
                return;
            }

            this.removeRequest(request);
            request.deferred.reject(error);
        });
    }

    private handleOutput(frame: VideoFrame) {
        const request = this.pendingDecodes.shift();

        if (!request) {
            frame.close();

            return;
        }

        if (frame.timestamp !== request.expectedTimestamp) {
            frame.close();
            request.deferred.reject(
                new Error(`decoded timestamp mismatch: expected ${request.expectedTimestamp}, got ${frame.timestamp}`),
            );

            return;
        }

        request.deferred.resolve(frame);
    }

    private handleError(error: unknown) {
        this.rejectPending(error);
    }

    private rejectPending(error: unknown) {
        while (this.pendingDecodes.length > 0) {
            this.pendingDecodes.shift()?.deferred.reject(error);
        }
    }

    private removeRequest(request: DecodeRequest) {
        const index = this.pendingDecodes.indexOf(request);

        if (index !== -1) {
            this.pendingDecodes.splice(index, 1);
        }
    }
}
